<template>
  <div class="collisions shadow text-dark">
    <div class="collisions__header">
      <h2>{{ i18n[locale]['double_bookings'] }}, {{ formattedDate }}</h2>
      <div class="collisions__date-changer">
        <button @click.prevent="$emit('prevDay')"><span class="icon-arrow-left4"></span></button>
        <button @click.prevent="$emit('nextDay')"><span class="icon-arrow-right4"></span></button>
      </div>
    </div>
    <ul class="collisions__content">
      <li class="collisions__lesson collisions__lesson__header">
        <div>{{ collisions.length }} <span v-if="collisions.length === 1">{{ i18n[locale]['double_booking'] }}</span><span v-else>{{ i18n[locale]['double_bookings'] }}</span> </div>
      </li>
      <li v-if="isLoading" class="collisions__loader">
        <Loader bg-color="#fff"></Loader>
      </li>
      <li class="collisions__lesson text-explainer" v-if="!isLoading && eventsWithOverlappingEvents.length < 1">
        {{ i18n[locale]['no_double_bookings'] }}
      </li>
      <li class="collisions__lesson"
          @click.stop="activeEvent = event"
          v-for="event in eventsWithOverlappingEvents"
          :key="event.event_id"
          :style="{'border-left-color': event.color}">
        <div>
          {{ event.v_start }} - {{ event.v_end }} | <b>{{ event.title }}</b>, <i>{{
            event.v_owner_calendar_name
          }}</i>
        </div>
        <div class="overlapping-calendars">
          <div v-for="overlappingCalendar in event.v_overlapping_cals_with_events"
               :key="overlappingCalendar.calendar.id"
               class="overlapping-calendar shadow"
               v-if="overlappingCalendar.idx < 3"
               @click.stop="activeCalendar = overlappingCalendar.calendar"
               :content="overlappingCalendar.lessons.map(overlappingCalendarEvent => overlappingCalendarEvent.v_event_title)"
               v-tippy="{
                 arrow: true,
                 zIndex: '999999992',
                 boundary: 'window'}">
            <span class="icon-warning"></span>
            <span>{{ overlappingCalendar.calendar.name ? overlappingCalendar.calendar.name : '' }}</span>
          </div>
          <div v-if="event.v_number_of_overlapping_events > 3">
            + {{ event.v_number_of_overlapping_events - 3 }} {{ i18n[locale]['more'] }}
          </div>
        </div>
      </li>
    </ul>
    <CalendarViewDay
      v-if="activeCalendar"
      :date="date"
      :calendar="activeCalendar"
      @close="activeCalendar = null">
    </CalendarViewDay>
    <transition name="zoomIn">
      <DCalendarShowEvent
        v-if="activeEvent"
        :event="activeEvent"
        :initCalendarId="activeEvent.calendar_id"
        :showSettings="false"
        @updated="onEventUpdated"
        @close="closeShowEvent">
      </DCalendarShowEvent>
    </transition>
  </div>
</template>

<script>
import Loader from '../common/Loader'
import docendo from '../../docendo'
import {mapGetters} from 'vuex'
import Moment from 'moment'
import {extendMoment} from 'moment-range'
import CalendarViewDay from '../Calendars/CalendarViewDay'
import DCalendarShowEvent from '../DCalendar/DCalendarShowEvent'

const moment = extendMoment(Moment)

const I18N = {
  da: {
    double_booking: 'Dobbeltbooking',
    double_bookings: 'Dobbeltbookinger',
    no_double_bookings: 'Ingen dobbeltbookinger denne dato',
    more: 'mere'
  },
  en: {
    double_booking: 'Doublebooking',
    double_bookings: 'Doublebookings',
    no_double_bookings: 'There is no double bookings this date',
    more: 'more'
  },
  nb: {
    double_booking: 'Dobbeltbooking',
    double_bookings: 'Dobbeltbookinger',
    no_double_bookings: 'Ingen dobbeltbookinger denne datoen',
    more: 'til'
  }
}

export default {
  components: {CalendarViewDay, Loader, DCalendarShowEvent},
  props: {
    date: {
      default: () => moment().startOf('day')
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      activeTab: 'teachers',
      collisions: [],
      eventsWithOverlappingEvents: [],
      startDate: moment().startOf('day'),
      endDate: moment().endOf('day'),
      isLoading: true,
      activeCalendar: null,
      locale: window.USER.language ? window.USER.language : 'en',
      activeEvent: null
    }
  },
  computed: {
    ...mapGetters([
      'allCalendarsInHash',
      'userCalendars',
      'roomCalendars'
    ]),
    isLoadingSchoolCalenders () {
      return this.$store.state.isLoadingSchoolCalenders
    },
    allCalendars () {
      return this.$store.state.calendars
    },
    calendarIds () {
      return this.allCalendars.map(calendar => calendar.id)
    },
    formattedDate () {
      return moment(this.date).format('LL')
    },
    i18n () {
      return I18N
    }
  },
  watch: {
    isLoadingSchoolCalenders (val) {
      if (!val) {
        this.readCollisions()
      }
    },
    date (val) {
      this.startDate = moment(val).startOf('day')
      this.endDate = moment(val).endOf('day')
      this.readCollisions()
    },
    show (val) {
      if (val) {
        console.log('collisions:update')
        this.readCollisions()
      }
    }
  },
  methods: {
    closeShowEvent () {
      this.activeEvent = null
    },
    onEventUpdated () {
      this.readCollisions()
      this.$emit('updated')
    },
    setActiveTab (tab) {
      this.activeTab = tab
      this.readCollisions()
    },
    readCollisions () {
      this.isLoading = true
      if (this.calendarIds.length < 1) {
        return
      }
      docendo.eventsFromCalendars(this.calendarIds, this.startDate, this.endDate, true, success => {
        let data = success && success.length > 0 ? JSON.parse(success) : []
        data = data.filter(event => event.event_type !== 'presence')
        this.collisions = data.sort((a, b) => moment(a.start).unix() - moment(b.start).unix())
        this.isLoading = false
        this.setCollisionsInGroups(this.collisions)
      }, failure => {
        console.log('failure', failure)
        this.isLoading = false
      })
    },
    setCollisionsInGroups (eventsArr) {
      this.eventsWithOverlappingEvents = []
      // Find alle events der overlapper og har samme kalendere på
      eventsArr.forEach(event => {
        event.v_start = moment(event.start).format('LT')
        event.v_end = moment(event.end).format('LT')
        event.v_overlapping_calendars = []
        event.v_overlapping_cals_with_events = {}
        event.v_calendarIds = event.calendars.map(cal => cal.calendar_id)
        event.v_owner_calendar_name = ''
        event.v_number_of_overlapping_events = 0
        if (this.allCalendarsInHash[event.calendar_id]) {
          event.v_owner_calendar_name = this.allCalendarsInHash[event.calendar_id].name
        }
        const range1 = moment.range(event.start, event.end)
        eventsArr.forEach(lesson => {
          if (lesson.event_id !== event.event_id) {
            const range2 = moment.range(lesson.start, lesson.end)
            // The events overlaps
            if (range1.overlaps(range2)) {
              const calIds = lesson.calendars.map(cal => cal.calendar_id)
              event.calendars.forEach(eventCal => {
                const overLapCalId = eventCal.calendar_id
                // Do they share calendars?
                if (calIds.indexOf(overLapCalId) > -1) {
                  if (this.allCalendarsInHash[overLapCalId]) {
                    if (!event.v_overlapping_cals_with_events[overLapCalId]) {
                      event.v_overlapping_cals_with_events[overLapCalId] = {
                        idx: Object.keys(event.v_overlapping_cals_with_events).length,
                        calendar: this.allCalendarsInHash[overLapCalId],
                        lessons: [{v_event_id: lesson.event_id, v_event_title: lesson.title}]
                      }
                    } else {
                      event.v_overlapping_cals_with_events[overLapCalId].lessons.push({
                        v_event_id: lesson.event_id,
                        v_event_title: lesson.title
                      })
                    }
                  }
                  event.v_overlapping_calendars.push(eventCal.calendar_id)
                }
              })
            }
          }
          event.v_number_of_overlapping_events = Object.keys(event.v_overlapping_cals_with_events).length
        })
        this.eventsWithOverlappingEvents.push(event)
      })
    }
  },
  mounted () {
    if (this.isLoadingSchoolCalenders) {
      return
    }
    this.readCollisions()
  }
}
</script>

<style>
@import '../../assets/css/variables.css';

.collisions {
  width: 100%;
  background: #ffffff;
  border-radius: 0.25rem;
}

.collisions__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 1rem;
}

.collisions__lesson {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-top: 0;
  border-left: 2px solid transparent;
  cursor: pointer;
  overflow-x: auto;
}

.collisions__lesson.text-explainer,
.collisions__lesson.collisions__lesson__header{
  cursor: default;
}

.collisions__lesson:hover:not(.text-explainer):not(.collisions__lesson__header) {
  background-color: aliceblue;
}

.collisions__lesson:first-child {
  border-top: 1px solid var(--borderColor);
}

.collisions__lesson:last-child {
  border-bottom: 1px solid var(--border-color);
  border-radius: 0 0 4px 4px;
}

.overlapping-calendars {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.overlapping-calendar {
  display: flex;
  align-items: center;
  border: 1px solid #eee;
  border-radius: 0.5rem;
  cursor: pointer;
  padding: 4px 8px;
  transition: background-color 0.25s;
  background-color: #ffffff;
}

.overlapping-calendar > span {
  margin-right: 4px;
}

.overlapping-calendar:hover {
  background-color: #eeeeee;
}

.collisions__lesson.collisions__lesson__header {
  background: #f9fafb;
  border: 1px solid var(--border-color);
  font-weight: bold;
  text-transform: uppercase;
  font-size: 11px;
  color: #8c919c;
}

.collisions__date-changer {
  display: flex;
}

.collisions__date-changer > button {
  margin-left: 0.25rem;
  display: flex;
  align-items: center;
}

.collisions__loader {
  display: flex;
}

.collisions__content {
  margin: 0;
}
</style>



// WEBPACK FOOTER //
// Collisions.vue?23165b88